/**
 * De-ice detection from METAR / field weather and cost posting.
 *
 * Reads the existing weather brief / METAR already on the desk —
 * does not fetch weather itself or invent a parallel stack.
 *
 * De-ice conditions trigger when:
 *  1. OAT (from METAR temperature) is at or below the company threshold, AND
 *  2. Precipitation / moisture present (visible moisture per ICAO holdover).
 *
 * Settings-driven: threshold, flat rate, delay minutes, enforcement.
 * A threshold of 0 means the check is disabled.
 */
import type { FieldWx } from "./weather";
import type { AwcHazardBrief } from "./awcHazards";
import type { OpsConstraintEnforcement, OpsDeskSettings } from "./types";

const METAR_TEMP_RE = /\b(M?\d{2})\/(M?\d{2})\b/;

const FREEZING_WX_RE =
  /\b(?:\+|-|VC)?(?:MI|PR|BC|DR|BL|SH|TS|FZ|RE)*(?:SN|SG|IC|PL|GR|GS|FZRA|FZDZ|FZFG|RA|DZ|BR|FG)\b/;

export type DeiceVerdict = {
  required: boolean;
  oatC: number | null;
  hasMoisture: boolean;
  severity: "ok" | OpsConstraintEnforcement;
  delayMin: number;
  costAmount: number;
  message: string;
};

/**
 * Parse OAT from a raw METAR string.
 * Returns Celsius or null when the token is missing.
 */
export function oatFromMetar(metar: string): number | null {
  const m = metar.match(METAR_TEMP_RE);
  if (!m) return null;
  const raw = m[1] ?? "";
  const neg = raw.startsWith("M");
  const n = Number(neg ? raw.slice(1) : raw);
  if (!Number.isFinite(n)) return null;
  return neg ? -n : n;
}

/**
 * Detect visible moisture in a METAR that matters for holdover.
 * Snow, ice pellets, freezing rain/drizzle/fog, rain, drizzle, mist, fog.
 */
export function metarHasMoisture(metar: string): boolean {
  return FREEZING_WX_RE.test(metar);
}

/**
 * Evaluate whether de-ice is required for a departure.
 *
 * Pure function — does not fetch weather. Reads fields already on the desk.
 * Disabled when `deiceTempThresholdC` is 0 (the default).
 */
export function evaluateDeice(input: {
  ops: Pick<OpsDeskSettings, "deiceTempThresholdC" | "deiceEnforcement" | "deiceDelayMin" | "costRates">;
  depField?: FieldWx | null;
}): DeiceVerdict {
  const ok: DeiceVerdict = {
    required: false,
    oatC: null,
    hasMoisture: false,
    severity: "ok",
    delayMin: 0,
    costAmount: 0,
    message: "",
  };

  const threshold = input.ops.deiceTempThresholdC;
  if (!threshold && threshold !== 0) return ok;
  if (threshold === 0) return ok;

  const metar = input.depField?.metar ?? "";
  if (!metar) return ok;

  const oatC = oatFromMetar(metar);
  if (oatC == null) return ok;

  ok.oatC = oatC;
  if (oatC > threshold) return ok;

  const hasMoisture = metarHasMoisture(metar);
  ok.hasMoisture = hasMoisture;
  if (!hasMoisture) return ok;

  const costRate = input.ops.costRates.deice;
  const delayMin = input.ops.deiceDelayMin;
  const costAmount = costRate > 0 ? -Math.round(costRate * 100) / 100 : 0;
  const icao = (input.depField?.icao ?? "").toUpperCase();
  const bits: string[] = [`OAT ${oatC}°C ≤ ${threshold}°C`];
  if (delayMin > 0) bits.push(`est. delay ${delayMin} min`);
  if (costRate > 0) bits.push(`charge $${costRate.toFixed(2)}`);

  return {
    required: true,
    oatC,
    hasMoisture,
    severity: input.ops.deiceEnforcement === "block" ? "block" : "flag",
    delayMin,
    costAmount,
    message: `DE-ICE  ${icao} ${bits.join(" · ")}.`,
  };
}
